import {
  useUserAddressesQuery,
  useMeQuery,
  useUpdateOrCreateUserAddressMutation,
  Enum_Componentuserdataaddress_Address_Type,
} from "@/generated";
import { motion } from "framer-motion";
import { useEffect, useState, Dispatch, useCallback } from "react";
import { useForm } from "react-hook-form";
import { toast } from "react-toastify";
import WButton from "../wbutton/WButton";
import WCheckbox from "../wcheckbox/WCheckbox";
import WDataWrapper from "../wdata-wrapper/WDataWrapper";
import WIconButton from "../wicon-button/WIconButton";
import WSelect from "../WSelect";
import WTextInput from "../wtext-input/WTextInput";
import WTypography from "../wtypography/WTypography";
import countries from "i18n-iso-countries";
import { useTranslation } from "react-i18next";
import { roCounties } from "@/lib/utils";
import WStoreAddress from "../wstore-address/WStoreAddress";
import find from "lodash/find";
import roCountriesData from "i18n-iso-countries/langs/ro.json";

/* eslint-disable-next-line */
export interface WUserAddressesProps {
  selectedAddressId: Dispatch<string>;
  skipCompanyAddresses?: boolean;
}

interface IFormInputs {
  title: string;
  isCompany: boolean;
  firstName: string;
  lastName: string;
  country: string;
  region: string;
  city: string;
  phone: string;
  streetName: string;
  streetNumber: string;
  blockNumber: string;
  stairNumber: string;
  floorNumber: string;
  apartmentNumber: string;
  companyName: string;
  companyRegNumber: string;
  companyCUI: string;
  companyAddress: string;
  selectedAddress: string;
}

export const WUserAddresses = (props: WUserAddressesProps) => {
  const { t: tStore } = useTranslation("store");
  const { t: tCommon } = useTranslation("common");

  const { data: meData, loading: meLoading } = useMeQuery();
  const userId = meData?.me?.id;

  countries.registerLocale(roCountriesData);
  const roCountries = Object.entries(countries.getNames("ro"));

  const {
    data: userData,
    loading: userLoading,
    error: userError,
    refetch: refetchAddresses,
  } = useUserAddressesQuery({
    fetchPolicy: "no-cache",
    skip: !userId,
    variables: {
      usersPermissionsUserId: userId ?? "",
    },
  });

  const {
    register,
    watch,
    handleSubmit,
    reset: resetAddressFields,
    formState: { errors, isValid },
  } = useForm<IFormInputs>({
    mode: "all",
  });

  const [editMode, setEditMode] = useState(Boolean);

  const [editAddressId, setEditAddressId] = useState<string | undefined>();
  const [currentlyEditingAddress, setCurrentlyEditingAddress] = useState<any>();

  const handleOpenEditModal = useCallback(
    (addressId: string) => {
      const currentAddress = find(
        userData?.usersPermissionsUser?.data?.attributes?.addresses,
        {
          id: addressId,
        }
      );

      setEditMode(true);
      resetAddressFields({
        isCompany: currentAddress?.is_company === true,
      });
      setEditAddressId(addressId);
      setCurrentlyEditingAddress(currentAddress);
    },
    [
      resetAddressFields,
      userData?.usersPermissionsUser?.data?.attributes?.addresses,
    ]
  );

  const cancelUpdateAddress = () => {
    setEditAddressId(undefined);
    setEditMode(false);
  };

  const [
    updateOrCreateAddressMutation,
    { loading: loadingUpdateOrCreateAddressMutation },
  ] = useUpdateOrCreateUserAddressMutation();

  // get and set existing addresses ids
  const [existingAddressesIds, setExistingAddressesIds] = useState(
    userData?.usersPermissionsUser?.data?.attributes?.addresses?.map(
      (address) => ({ id: address?.id })
    )
  );

  useEffect(() => {
    setExistingAddressesIds(
      userData?.usersPermissionsUser?.data?.attributes?.addresses?.map(
        (address) => ({ id: address?.id })
      )
    );
  }, [userData?.usersPermissionsUser?.data?.attributes?.addresses]);

  const createOrUpdateAddress = useCallback(() => {
    const oldAddresses =
      userData?.usersPermissionsUser?.data?.attributes?.addresses?.map(
        (address) => {
          const { __typename, ...rest } = address as { __typename: string };
          return rest;
        }
      );

    if (userId && oldAddresses) {
      updateOrCreateAddressMutation({
        onCompleted: (response) => {
          refetchAddresses();
          toast.success(tStore("address-added"));
          setEditMode(false);
          setEditAddressId(undefined);

          props?.selectedAddressId &&
            response?.updateUsersPermissionsUser?.data?.attributes
              ?.addresses?.[0]?.id &&
            props?.selectedAddressId(
              response?.updateUsersPermissionsUser?.data?.attributes
                ?.addresses?.[0]?.id
            );
        },
        variables: {
          updateUsersPermissionsUserId: userId,
          data: {
            addresses: [
              ...(existingAddressesIds || []),
              {
                id: editAddressId,
                address_type:
                  Enum_Componentuserdataaddress_Address_Type.Billing,
                apartment_number: watch("apartmentNumber"),
                block_number: watch("blockNumber"),
                city: watch("city"),
                company_address: watch("companyAddress"),
                company_identification_number: watch("companyCUI"),
                company_name: watch("companyName"),
                company_registration_number: watch("companyRegNumber"),
                county: watch("region"),
                first_name: watch("firstName"),
                floor_number: watch("floorNumber"),
                is_company: watch("isCompany"),
                last_name: watch("lastName"),
                phone: watch("phone"),
                stair_number: watch("stairNumber"),
                street: watch("streetName"),
                street_number: watch("streetNumber"),
                title: watch("streetName"),
              },
            ],
          },
        },
      });
    }
  }, [
    editAddressId,
    existingAddressesIds,
    props,
    refetchAddresses,
    tStore,
    updateOrCreateAddressMutation,
    userData?.usersPermissionsUser?.data?.attributes?.addresses,
    userId,
    watch,
  ]);

  const submitAddress = useCallback(() => {
    createOrUpdateAddress();
  }, [createOrUpdateAddress]);

  const onAdd = () => {
    setEditMode(true);
    resetAddressFields();
    setCurrentlyEditingAddress(undefined);
  };

  props?.selectedAddressId(watch("selectedAddress"));

  return (
    <WDataWrapper
      isData={!!userData}
      isLoading={userLoading || meLoading}
      isError={userError}
    >
      <div>
        <div className="flex flex-col w-full gap-5 p-5 mx-auto rounded-md bg-eklight">
          <span className="text-sm font-bold">{tStore("your-addresses")}</span>
          {!editMode && (
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              className="flex flex-col gap-5"
            >
              {userData?.usersPermissionsUser?.data?.attributes?.addresses?.map(
                (address) =>
                  address &&
                  (!props.skipCompanyAddresses || !address.is_company) && (
                    <div key={address?.id} className="flex gap-3">
                      <WCheckbox
                        {...register("selectedAddress")}
                        id={address?.id}
                        onChange={async () => {
                          props.selectedAddressId(address?.id);
                        }}
                        defaultChecked={
                          userData?.usersPermissionsUser?.data?.attributes?.addresses?.at(
                            -1
                          )?.id === address?.id
                        }
                        value={address?.id}
                        name="selectedAddress"
                        className="w-full"
                        radio
                        label={
                          <WStoreAddress
                            title={address?.title}
                            firstName={address?.first_name}
                            lastName={address?.last_name}
                            phone={address?.phone}
                            street={address?.street}
                            streetNumber={address?.street_number}
                            blockNumber={address?.block_number}
                            stairNumber={address?.stair_number}
                            apartmentNumber={address?.apartment_number}
                            county={address?.county}
                            city={address?.city}
                            country={"RO"}
                            companyName={address?.company_name}
                            companyRegNumber={
                              address?.company_registration_number
                            }
                            companyIdentificationNumber={
                              address?.company_identification_number
                            }
                            companyAddress={address?.company_address}
                          />
                        }
                      />

                      <div>
                        <WIconButton
                          icon="edit"
                          type="transparent"
                          onClick={() => handleOpenEditModal(address?.id)}
                        />
                      </div>
                    </div>
                  )
              )}
              <div>
                <WButton
                  icon="plus"
                  type="secondary"
                  text={tCommon("add")}
                  onClick={onAdd}
                />
              </div>
            </motion.div>
          )}

          {editMode && (
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              key={editAddressId}
            >
              <form onSubmit={handleSubmit(submitAddress)}>
                <div className="flex flex-col gap-5">
                  <div className="flex justify-between gap-2">
                    <WTypography
                      text={tStore(
                        `${editAddressId ? "edit" : "add"}-address`
                      )?.toString()}
                      type="subtitle"
                    />
                    <WIconButton
                      type="white"
                      icon="xmark"
                      onClick={cancelUpdateAddress}
                    />
                  </div>
                  <div className="flex flex-col gap-5 lg:flex-row">
                    <div className="w-full">
                      <WTextInput
                        {...register("firstName", {
                          required: {
                            value: true,
                            message: tStore("required"),
                          },
                        })}
                        placeholder={tStore("first-name")?.toString()}
                        customClass="border-b"
                        defaultValue={
                          currentlyEditingAddress?.first_name ??
                          userData?.usersPermissionsUser?.data?.attributes
                            ?.firstName
                        }
                        required
                        error={errors?.firstName}
                      />
                    </div>
                    <div className="w-full">
                      <WTextInput
                        {...register("lastName", {
                          required: {
                            value: true,
                            message: tStore("required"),
                          },
                        })}
                        placeholder={tStore("last-name")?.toString()}
                        customClass="border-b"
                        defaultValue={
                          currentlyEditingAddress?.last_name ??
                          userData?.usersPermissionsUser?.data?.attributes
                            ?.lastName
                        }
                        required
                        error={errors?.lastName}
                      />
                    </div>
                  </div>
                  <div className="flex flex-col gap-5 lg:flex-row">
                    <div className="w-full lg:w-1/2">
                      <WSelect
                        {...register("country", {
                          required: {
                            value: true,
                            message: tStore("required"),
                          },
                        })}
                        label={tStore("country")?.toString()}
                        defaultValue={currentlyEditingAddress?.country ?? "RO"}
                        options={roCountries?.map(
                          ([countryCode, countryName]) => ({
                            label: countryName,
                            value: countryCode,
                          })
                        )}
                        required
                        error={errors?.country}
                      />
                    </div>
                    {watch("country") === "RO" && (
                      <div className="w-full lg:w-1/2">
                        <WSelect
                          {...register("region")}
                          label={tStore("county-region")?.toString()}
                          options={roCounties?.map((county) => ({
                            label: county?.countyName,
                            value: county?.countyCode,
                          }))}
                          defaultValue={currentlyEditingAddress?.county}
                          error={errors?.region}
                        />
                      </div>
                    )}
                  </div>

                  <div className="flex flex-col gap-5 lg:flex-row">
                    <div className="w-full">
                      <WTextInput
                        {...register("city", {
                          required: {
                            value: true,
                            message: tStore("required"),
                          },
                        })}
                        placeholder={tStore("city")?.toString()}
                        customClass="border-b"
                        defaultValue={
                          currentlyEditingAddress?.city ??
                          userData?.usersPermissionsUser?.data?.attributes?.city
                        }
                        required
                        error={errors?.city}
                      />
                    </div>
                    <div className="w-full">
                      <WTextInput
                        {...register("phone", {
                          required: {
                            value: true,
                            message: tStore("required"),
                          },
                        })}
                        placeholder={tStore("phone")?.toString()}
                        customClass="border-b"
                        type="number"
                        defaultValue={currentlyEditingAddress?.phone}
                        required
                        error={errors?.phone}
                      />
                    </div>
                  </div>

                  <div className="flex flex-col gap-5 lg:flex-row">
                    <div className="w-full">
                      <WTextInput
                        {...register("streetName", {
                          required: {
                            value: true,
                            message: tStore("required"),
                          },
                        })}
                        placeholder={tStore("street")?.toString()}
                        customClass="border-b"
                        defaultValue={currentlyEditingAddress?.street}
                        required
                        error={errors?.streetName}
                      />
                    </div>
                  </div>

                  <div className="flex flex-col gap-5 lg:flex-row">
                    <div className="w-full">
                      <WTextInput
                        {...register("streetNumber")}
                        placeholder={tStore("number")?.toString()}
                        customClass="border-b"
                        defaultValue={currentlyEditingAddress?.street_number}
                      />
                    </div>
                    <div className="w-full">
                      <WTextInput
                        {...register("blockNumber")}
                        placeholder={tStore("block-number")?.toString()}
                        customClass="border-b"
                        defaultValue={currentlyEditingAddress?.block_number}
                      />
                    </div>
                    <div className="w-full">
                      <WTextInput
                        {...register("stairNumber")}
                        placeholder={tStore("stair-number")?.toString()}
                        customClass="border-b"
                        defaultValue={currentlyEditingAddress?.stair_number}
                      />
                    </div>
                    <div className="w-full">
                      <WTextInput
                        {...register("floorNumber")}
                        placeholder={tStore("floor-number")?.toString()}
                        customClass="border-b"
                        defaultValue={currentlyEditingAddress?.floor_number}
                      />
                    </div>
                    <div className="w-full">
                      <WTextInput
                        {...register("apartmentNumber")}
                        placeholder={tStore("apartment")?.toString()}
                        customClass="border-b"
                        defaultValue={currentlyEditingAddress?.apartment_number}
                      />
                    </div>
                  </div>

                  {!props.skipCompanyAddresses && (
                    <div>
                      <div className="flex gap-5">
                        <WCheckbox
                          value=""
                          label={`${tStore("legal-person")}?`}
                          id="is-company"
                          {...register("isCompany")}
                        />
                      </div>
                      {watch("isCompany") === true && (
                        <>
                          <div className="flex flex-col gap-5 lg:flex-row">
                            <div className="w-full">
                              <WTextInput
                                {...register("companyName", {
                                  required: watch("isCompany") === true,
                                })}
                                placeholder={tStore("company-name")?.toString()}
                                required
                                customClass="border-b"
                                defaultValue={
                                  currentlyEditingAddress?.company_name
                                }
                              />
                            </div>
                            <div className="w-full">
                              <WTextInput
                                {...register("companyRegNumber", {
                                  required: watch("isCompany") === true,
                                })}
                                required
                                placeholder={tStore(
                                  "company-reg-number"
                                )?.toString()}
                                customClass="border-b"
                                defaultValue={
                                  currentlyEditingAddress?.company_registration_number
                                }
                              />
                            </div>
                          </div>
                          <div className="flex flex-col gap-5 lg:flex-row">
                            <div className="w-full">
                              <WTextInput
                                {...register("companyCUI", {
                                  required: watch("isCompany") === true,
                                })}
                                required
                                placeholder={tStore("company-cui")?.toString()}
                                customClass="border-b"
                                defaultValue={
                                  currentlyEditingAddress?.company_identification_number
                                }
                              />
                            </div>
                            <div className="w-full">
                              <WTextInput
                                {...register("companyAddress", {
                                  required: watch("isCompany") === true,
                                })}
                                required
                                placeholder={tStore(
                                  "company-address"
                                )?.toString()}
                                customClass="border-b"
                                defaultValue={
                                  currentlyEditingAddress?.company_address
                                }
                              />
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  )}
                  <div className="flex flex-col gap-5 lg:items-center lg:flex-row">
                    <WButton
                      buttonType="submit"
                      disabled={
                        !isValid || loadingUpdateOrCreateAddressMutation
                      }
                      text={tStore("save-address")}
                    />

                    <WButton
                      type="decline"
                      text={tStore("cancel")}
                      onClick={cancelUpdateAddress}
                    />
                  </div>
                </div>
              </form>
            </motion.div>
          )}
        </div>
      </div>
    </WDataWrapper>
  );
};

export default WUserAddresses;
